import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, AlertMessage } from "components/atoms";
import { EmailInput, PasswordInput } from "components/molecules";
import { useAuth } from "contexts";

const SignInSchema = Yup.object().shape({
  username: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    )
    .required("Password is required"),
});

const SignInForm: React.FC = () => {
  const { setAuthState, handleSignIn } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSignInError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    setErrorMessage(errorMessage);
  };

  const handleSignInSuccess = (): void => {
    signInFormik.resetForm();
  };

  const handleSignInSubmit = async (values: {
    username: string;
    password: string;
  }) => {
    try {
      const error = await handleSignIn({
        username: values.username,
        password: values.password,
      });

      if (error) {
        handleSignInError(error);
        return;
      }

      handleSignInSuccess();
    } catch (error) {
      handleSignInError(error);
    }
  };

  const signInFormik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: SignInSchema,
    onSubmit: handleSignInSubmit,
  });

  const toSignUp = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_UP",
    }));
  };

  const toResetPassword = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "RESET_PASSWORD",
    }));
  };

  return (
    <form onSubmit={signInFormik.handleSubmit} className="auth-form">
      <div className="form-group">
        <h2>Sign In</h2>
      </div>

      <EmailInput
        id="username"
        label="Email"
        getFieldProps={signInFormik.getFieldProps}
        touched={signInFormik.touched.username}
        error={signInFormik.errors.username}
      />

      <PasswordInput
        label="Password"
        id="password"
        placeholder="Please enter password"
        getFieldProps={signInFormik.getFieldProps}
        touched={signInFormik.touched.password}
        error={signInFormik.errors.password}
      />

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Sign In"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <div className="button-group">
        <Button
          type="button"
          text="Reset Password"
          onClick={toResetPassword}
          className="form-button"
          width="100%"
          variant="border"
        />

        <Button
          type="button"
          text="Go to Sign Up"
          onClick={toSignUp}
          className="form-button"
          width="100%"
          variant="border"
        />
      </div>
    </form>
  );
};

export default SignInForm;
